import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import axios from 'axios';
import {
  XMarkIcon,
  CreditCardIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

// Validate Stripe publishable key is configured
const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
if (!stripePublishableKey) {
  console.error('REACT_APP_STRIPE_PUBLIC_KEY is not configured');
}

const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

const BillingAuthModal = ({ 
  isOpen, 
  onClose, 
  billingDetails, 
  inviteData, 
  onSuccess,
  darkMode = false 
}) => {
  if (!isOpen) return null;

  if (!stripePromise) {
    return ReactDOM.createPortal(
      <div className="fixed inset-0 z-[99999] bg-black flex items-center justify-center p-4" style={{ backdropFilter: 'blur(4px)', backgroundColor: 'rgba(0, 0, 0, 0.85)' }}>
        <div 
          className="rounded-2xl max-w-md w-full p-6"
          style={{
            boxSizing: 'border-box',
            boxShadow: darkMode 
              ? 'inset 0px 0px 24px 1px rgba(255, 255, 255, 0.08)' 
              : 'inset 0px 0px 24px 1px rgba(0, 0, 0, 0.08)',
            backgroundColor: darkMode 
              ? 'rgba(255, 255, 255, 0.04)' 
              : 'rgba(0, 0, 0, 0.02)',
            border: darkMode 
              ? '1px solid rgba(255, 255, 255, 0.12)' 
              : '1px solid rgba(0, 0, 0, 0.12)',
            backdropFilter: 'blur(20px)',
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-black dark:text-white">
              Configuration Error
            </h3>
            <button
              onClick={onClose}
              className="transition-colors"
              style={{ color: darkMode ? '#888888' : '#6B6B6B' }}
              onMouseEnter={(e) => e.currentTarget.style.color = darkMode ? '#ffffff' : '#000000'}
              onMouseLeave={(e) => e.currentTarget.style.color = darkMode ? '#888888' : '#6B6B6B'}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="text-center">
            <ExclamationTriangleIcon className="mx-auto h-12 w-12 mb-4" style={{ color: '#ef4444' }} />
            <p className="text-sm" style={{ color: darkMode ? '#888888' : '#6B6B6B' }}>
              Stripe billing is not properly configured. Please contact support.
            </p>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[99999] bg-black flex items-center justify-center p-4" style={{ backdropFilter: 'blur(4px)', backgroundColor: 'rgba(0, 0, 0, 0.85)' }}>
      <div 
        className="rounded-2xl max-w-md w-full"
        style={{
          boxSizing: 'border-box',
          boxShadow: darkMode 
            ? 'inset 0px 0px 24px 1px rgba(255, 255, 255, 0.08)' 
            : 'inset 0px 0px 24px 1px rgba(0, 0, 0, 0.08)',
          backgroundColor: darkMode 
            ? 'rgba(255, 255, 255, 0.04)' 
            : 'rgba(0, 0, 0, 0.02)',
          border: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.12)' 
            : '1px solid rgba(0, 0, 0, 0.12)',
          backdropFilter: 'blur(20px)',
        }}
      >
        <Elements stripe={stripePromise}>
          <BillingAuthContent
            onClose={onClose}
            billingDetails={billingDetails}
            inviteData={inviteData}
            onSuccess={onSuccess}
            darkMode={darkMode}
          />
        </Elements>
      </div>
    </div>,
    document.body
  );
};

const BillingAuthContent = ({ onClose, billingDetails, inviteData, onSuccess, darkMode }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [useNewCard, setUseNewCard] = useState(false);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(true);

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/team-billing/payment-methods`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      setPaymentMethods(response.data.paymentMethods || []);
      if (response.data.paymentMethods?.length > 0) {
        setSelectedPaymentMethod(response.data.paymentMethods[0].id);
      } else {
        setUseNewCard(true);
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      setUseNewCard(true);
    } finally {
      setLoadingPaymentMethods(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError('');

    try {
      let paymentMethodId = selectedPaymentMethod;
      
      // If using new card, create payment method first
      if (useNewCard) {
        const cardElement = elements.getElement(CardElement);
        
        const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
        });

        if (pmError) {
          throw new Error(pmError.message);
        }

        paymentMethodId = paymentMethod.id;
      }

      // Process the team member invitation with billing
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/team-billing/authorize-and-invite-member`,
        {
          ...inviteData,
          paymentMethodId,
          billingDetails
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.requiresAction) {
        // Handle 3D Secure or other authentication
        const { error: confirmError } = await stripe.confirmCardPayment(
          response.data.clientSecret
        );

        if (confirmError) {
          throw new Error(confirmError.message);
        }

        // Retry the invitation after successful authentication
        const retryResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}/team-billing/authorize-and-invite-member`,
          {
            ...inviteData,
            paymentMethodId,
            billingDetails,
            paymentIntentId: response.data.paymentIntentId
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        onSuccess(retryResponse.data);
      } else {
        onSuccess(response.data);
      }

    } catch (error) {
      console.error('Error processing payment:', error);
      setError(error.response?.data?.message || error.message || 'Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: darkMode ? '#ffffff' : '#000000',
        '::placeholder': {
          color: darkMode ? '#888888' : '#6B6B6B',
        },
        backgroundColor: 'transparent',
      },
      invalid: {
        color: '#ef4444',
      },
    },
    hidePostalCode: true,
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <CreditCardIcon className="h-6 w-6 mr-2" style={{ color: darkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)' }} />
          <h3 className="text-lg font-medium text-black dark:text-white">
            Authorize Team Member Billing
          </h3>
        </div>
        <button
          onClick={onClose}
          className="transition-colors"
          style={{ color: darkMode ? '#888888' : '#6B6B6B' }}
          onMouseEnter={(e) => e.currentTarget.style.color = darkMode ? '#ffffff' : '#000000'}
          onMouseLeave={(e) => e.currentTarget.style.color = darkMode ? '#888888' : '#6B6B6B'}
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      {/* Billing Details */}
      <div 
        className="mb-6 p-4 rounded-lg"
        style={{
          backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)',
          border: darkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
        }}
      >
        <div className="flex items-start mb-3">
          <ExclamationTriangleIcon className="h-5 w-5 mr-2 mt-0.5" style={{ color: '#B0877B' }} />
          <div>
            <h4 className="font-medium text-black dark:text-white mb-1">
              {billingDetails?.isBulkInvite ? 'Bulk Team Member Addition' : 'Team Member Addition'} - {billingDetails?.billingCycle === 'annual' ? 'Annual' : 'Monthly'} Plan
            </h4>
            {billingDetails?.isBulkInvite ? (
              <div className="text-sm" style={{ color: darkMode ? '#888888' : '#6B6B6B' }}>
                <p>Adding {billingDetails.memberInfo?.validInvitations} team member{billingDetails.memberInfo?.validInvitations !== 1 ? 's' : ''}</p>
                {billingDetails.memberInfo?.freeInvites > 0 && (
                  <p>• {billingDetails.memberInfo.freeInvites} within plan limits (free)</p>
                )}
                {billingDetails.memberInfo?.billableInvites > 0 && (
                  <p>• {billingDetails.memberInfo.billableInvites} additional member{billingDetails.memberInfo.billableInvites !== 1 ? 's' : ''} (billable)</p>
                )}
              </div>
            ) : (
              <p className="text-sm" style={{ color: darkMode ? '#888888' : '#6B6B6B' }}>
                Adding: {inviteData?.firstName} {inviteData?.lastName} ({inviteData?.email})
              </p>
            )}
          </div>
        </div>
        
        <div className="space-y-2 text-sm">
          {billingDetails?.isBulkInvite ? (
            <>
              <div className="flex justify-between">
                <span style={{ color: darkMode ? '#888888' : '#6B6B6B' }}>Total members to add:</span>
                <span className="text-black dark:text-white font-medium">{billingDetails.memberInfo?.validInvitations}</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: darkMode ? '#888888' : '#6B6B6B' }}>Within plan limits (free):</span>
                <span className="text-black dark:text-white font-medium">{billingDetails.memberInfo?.freeInvites || 0}</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: darkMode ? '#888888' : '#6B6B6B' }}>Additional members (billable):</span>
                <span className="text-black dark:text-white font-medium">{billingDetails.memberInfo?.billableInvites || 0}</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: darkMode ? '#888888' : '#6B6B6B' }}>Cost per additional member:</span>
                <span className="text-black dark:text-white font-medium">{formatCurrency(billingDetails?.seatPrice)}</span>
              </div>
              <div 
                className="pt-2 flex justify-between font-medium"
                style={{
                  borderTop: darkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
                }}
              >
                <span className="text-black dark:text-white">Total amount to authorize:</span>
                <span className="text-black dark:text-white">{formatCurrency(billingDetails?.fullPrice)}</span>
              </div>
            </>
          ) : (
            <>
              <div className="flex justify-between">
                <span style={{ color: darkMode ? '#888888' : '#6B6B6B' }}>Full seat price:</span>
                <span className="text-black dark:text-white font-medium">{formatCurrency(billingDetails?.fullPrice)}</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: darkMode ? '#888888' : '#6B6B6B' }}>Authorization hold:</span>
                <span className="text-black dark:text-white font-medium">Full amount (for validation)</span>
              </div>
              <div 
                className="pt-2 flex justify-between font-medium"
                style={{
                  borderTop: darkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
                }}
              >
                <span className="text-black dark:text-white">Amount to authorize:</span>
                <span className="text-black dark:text-white">{formatCurrency(billingDetails?.fullPrice)}</span>
              </div>
            </>
          )}
        </div>

        <div 
          className="mt-3 p-3 rounded-lg"
          style={{
            backgroundColor: darkMode ? 'rgba(176, 135, 123, 0.15)' : 'rgba(176, 135, 123, 0.1)',
            border: darkMode ? '1px solid rgba(176, 135, 123, 0.3)' : '1px solid rgba(176, 135, 123, 0.25)',
          }}
        >
          <p className="text-xs" style={{ color: darkMode ? '#B0877B' : '#8B6B5A' }}>
            <strong>How billing works:</strong> {billingDetails?.isBulkInvite 
              ? `When members join, a prorated charge for the remaining days in your current billing period will be processed. Starting on your next billing date, the full seat price will be included in your regular subscription renewal.`
              : `When the team member joins, a prorated charge for the remaining days in your current billing period will be charged immediately. Starting on your next billing date, the full seat price will be included in your regular subscription renewal.`
            }
          </p>
        </div>

        {billingDetails?.nextBillingDate && (
          <p className="text-xs mt-2" style={{ color: darkMode ? '#666666' : '#999999' }}>
            Full seat price applies starting: {new Date(billingDetails.nextBillingDate).toLocaleDateString()}
          </p>
        )}
      </div>

      {error && (
        <div 
          className="mb-4 p-3 rounded-lg"
          style={{
            backgroundColor: darkMode ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)',
            border: darkMode ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid rgba(239, 68, 68, 0.25)',
          }}
        >
          <p className="text-sm font-medium" style={{ color: darkMode ? '#f87171' : '#dc2626' }}>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Payment Method Selection */}
        {!loadingPaymentMethods && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-black dark:text-white mb-2">
              Payment Method
            </label>
            
            {paymentMethods.length > 0 && (
              <div className="space-y-2 mb-3">
                {paymentMethods.map((pm) => (
                  <label key={pm.id} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value={pm.id}
                      checked={selectedPaymentMethod === pm.id && !useNewCard}
                      onChange={(e) => {
                        setSelectedPaymentMethod(e.target.value);
                        setUseNewCard(false);
                      }}
                      className="mr-2"
                      style={{
                        accentColor: darkMode ? '#B0877B' : '#8B6B5A',
                      }}
                    />
                    <span className="text-sm text-black dark:text-white">
                      **** **** **** {pm.card.last4} ({pm.card.brand.toUpperCase()})
                    </span>
                  </label>
                ))}
                
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="paymentMethod"
                    checked={useNewCard}
                    onChange={() => setUseNewCard(true)}
                    className="mr-2"
                    style={{
                      accentColor: darkMode ? '#B0877B' : '#8B6B5A',
                    }}
                  />
                  <span className="text-sm text-black dark:text-white">
                    Use a new card
                  </span>
                </label>
              </div>
            )}

            {/* New Card Form */}
            {useNewCard && (
              <div 
                className="p-3 rounded-lg"
                style={{
                  border: darkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.12)',
                  backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.02)',
                }}
              >
                <CardElement options={cardElementOptions} />
              </div>
            )}
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-normal rounded-full transition-all hover:opacity-80"
            disabled={processing}
            style={{
              backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)',
              color: darkMode ? '#ffffff' : '#000000',
              border: darkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.12)',
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={processing || !stripe || loadingPaymentMethods}
            className="px-4 py-2 text-sm font-normal rounded-full transition-all hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            style={{
              backgroundColor: darkMode ? '#ffffff' : '#000000',
              color: darkMode ? '#000000' : '#ffffff',
              border: 'none',
              cursor: (processing || !stripe || loadingPaymentMethods) ? 'not-allowed' : 'pointer',
            }}
          >
            {processing ? (
              <>
                <div 
                  className="animate-spin h-4 w-4 border-2 border-t-transparent rounded-full mr-2"
                  style={{
                    borderColor: darkMode ? '#000000' : '#ffffff',
                    borderTopColor: 'transparent',
                  }}
                ></div>
                Processing...
              </>
            ) : (
              <>
                <CheckCircleIcon className="h-4 w-4 mr-2" />
                Authorize {formatCurrency(billingDetails?.fullPrice)}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BillingAuthModal; 